import type * as CoursePage from '../types/CoursePage'
import type {
  RawSidebarComponent,
  SidebarComponent,
} from '../../sidebar-components/types'
import type { Component } from '../../components/types'
import parseComponent from '../../components/parsers'
import eventModuleComponentParser from '../../sidebar-components/parsers/EventModule'
import { socialMetaDataParser } from '.'
import errorLogging from 'services/error-logging'

function parseSidebarComponent(
  rawComponent: RawSidebarComponent,
  metaData: CoursePage.MetaData,
): SidebarComponent | undefined {
  switch (rawComponent.acfFcLayout) {
    case 'event_module':
      return eventModuleComponentParser(rawComponent, metaData)
    default:
      errorLogging(
        new Error(
          `missing sidebar component parser for ${rawComponent.acfFcLayout}`,
        ),
        { rawComponent, metaData },
      )
      return undefined
  }
}

function parseCourseDetails(rawResponse: CoursePage.Raw) {
  const { courseDetails } = rawResponse.tiles[0]

  if (!courseDetails) {
    return
  }

  const {
    address,
    city,
    country,
    currency,
    currencySymbol,
    id,
    state,
    locationUrl,
    name,
    postalCode,
    seminarUrl,
    startDate,
    status,
    isOnline,
    isWaitlisted,
    language = 'en',
  } = courseDetails

  const location =
    address && city && country && postalCode
      ? {
          address,
          city,
          country,
          state,
          postalCode,
        }
      : undefined

  return {
    language,
    location,
    currency,
    currencySymbol,
    id,
    locationUrl,
    name,
    seminarUrl,
    startDate,
    status,
    isOnlineSeminar: isOnline === 1,
    isWaitlisted: Boolean(isWaitlisted),
  }
}

export default function coursePageParser(
  rawResponse: CoursePage.Raw,
): CoursePage.Parsed {
  const { acf, title, breadcrumbs, contentType } = rawResponse.tiles[0]
  const { components, sidebar, fullPath } = acf

  const courseDetails = parseCourseDetails(rawResponse)

  const metaData = {
    breadcrumbs,
    contentType,
    title,
    courseDetails,
    socialMetaData: socialMetaDataParser(rawResponse),
    path: fullPath,
  }

  return {
    ...metaData,
    components: components
      .map(parseComponent)
      .filter((component): component is Component => !!component),
    sidebarComponents: sidebar
      ? sidebar
          .map((sidebarComponent) =>
            parseSidebarComponent(sidebarComponent, metaData),
          )
          .filter(
            (sidebarComponent): sidebarComponent is SidebarComponent =>
              !!sidebarComponent,
          )
      : undefined,
  }
}
